import React, { useState, useEffect } from 'react';
import { FiBell, FiHelpCircle, FiUser, FiChevronDown, FiHome, FiBarChart2, FiTruck, FiClock, FiExternalLink, FiCheckCircle, FiXCircle, FiArrowLeft, FiArrowRight, FiMap, FiDownload, FiTool, FiMoon, FiPlus, FiPackage, FiLayers, FiTrendingUp, FiAlertTriangle, FiLogOut } from 'react-icons/fi';
import { FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn } from 'react-icons/fa';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip, BarChart, CartesianGrid, XAxis, YAxis, Bar, Legend, LineChart, Line } from 'recharts';
import { PedidosService } from '../services/pedidosService';
import { ChoferesService } from '../services/choferesService';
import { FletesService } from '../services/fletesService';
import GestionChoferesGerente from './GestionChoferesGerente';
import './DashboardGerente.css';
import { CombustibleService } from '../services/combustibleService';
import ReporteCombustible from './ReporteCombustible';
import FormularioAutos from './FormularioAutos';
import ReporteMantenimiento from './ReporteMantenimiento';
import GestionAlertas from './GestionAlertas';

const DashboardGerente = () => {
  const [pedidos, setPedidos] = useState([]);
  const [choferes, setChoferes] = useState([]);
  const [choferSeleccionado, setChoferSeleccionado] = useState('');
  const [vistaActual, setVistaActual] = useState('dashboard'); // 'dashboard' | 'choferes' | 'autos' | 'combustible' | 'mantenimiento' | 'alertas'
  const [estadisticas, setEstadisticas] = useState({
    entregados: 0,
    pendientes: 0,
    cancelados: 0,
    total: 0
  });
  const [consumos, setConsumos] = useState([]);
  const [mostrarPedidosPendientes, setMostrarPedidosPendientes] = useState(false);
  const [busquedaFolio, setBusquedaFolio] = useState('');
  const [alertas, setAlertas] = useState([]);
  const [fletes, setFletes] = useState([]);
  
  // Estados para paginación de pedidos
  const [paginaActual, setPaginaActual] = useState(1);
  const [pedidosPorPagina] = useState(5); // Número de pedidos por página
  
  // Estados para filtros de exportación
  const [filtroTiempo, setFiltroTiempo] = useState('mes'); // 'dia', 'mes', 'año'
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [mesSeleccionado, setMesSeleccionado] = useState(new Date().getMonth() + 1);
  const [añoSeleccionado, setAñoSeleccionado] = useState(new Date().getFullYear());

  useEffect(() => {
    const unsubscribePedidos = PedidosService.suscribirsePedidos(setPedidos);
    const unsubscribeChoferes = ChoferesService.suscribirseChoferes(setChoferes);
    const unsubscribeComb = CombustibleService.suscribirseRegistros(setConsumos);
    const unsubscribeFletes = FletesService.suscribirseFletes(setFletes);

    return () => {
      unsubscribePedidos();
      unsubscribeChoferes();
      if (typeof unsubscribeComb === 'function') unsubscribeComb();
      if (typeof unsubscribeFletes === 'function') unsubscribeFletes();
    };
  }, []);

  useEffect(() => {
    if (pedidos.length > 0) {
      const stats = {
        entregados: pedidos.filter(p => p.estatus === 'entregado').length,
        pendientes: pedidos.filter(p => p.estatus === 'en_preparacion' || p.estatus === 'ruta' || p.estatus === 'pendiente').length,
        cancelados: pedidos.filter(p => p.estatus === 'cancelado').length,
        total: pedidos.length
      };
      setEstadisticas(stats);
    }
  }, [pedidos]);

  // Calcular alertas de pedidos
  useEffect(() => {
    const calcularAlertas = () => {
      const ahora = new Date();
      
      const pedidosConAlerta = pedidos
        .filter(pedido => {
          // Excluir pedidos que ya están entregados
          if (pedido.estatus === 'entregado') {
            return false;
          }

          // Obtener la última fecha de actualización
          const fechaActualizacion = pedido.fechaActualizacion 
            ? new Date(pedido.fechaActualizacion).getTime()
            : pedido.timestamp 
              ? pedido.timestamp
              : pedido.fechaCompra 
                ? new Date(pedido.fechaCompra).getTime()
                : null;

          if (!fechaActualizacion) return false;

          // Verificar si han pasado más de 24 horas desde la última actualización
          const horasSinActualizar = (ahora.getTime() - fechaActualizacion) / (1000 * 60 * 60);
          
          return horasSinActualizar >= 24;
        })
        .map(pedido => {
          const fechaActualizacion = pedido.fechaActualizacion 
            ? new Date(pedido.fechaActualizacion).getTime()
            : pedido.timestamp 
              ? pedido.timestamp
              : new Date(pedido.fechaCompra).getTime();

          const horasSinActualizar = (ahora.getTime() - fechaActualizacion) / (1000 * 60 * 60);
          const diasSinActualizar = Math.floor(horasSinActualizar / 24);
          const horasRestantes = Math.floor(horasSinActualizar % 24);

          return {
            ...pedido,
            horasSinActualizar: Math.floor(horasSinActualizar),
            diasSinActualizar,
            horasRestantes,
            fechaUltimaActualizacion: pedido.fechaActualizacion || pedido.fechaCompra
          };
        })
        .sort((a, b) => b.horasSinActualizar - a.horasSinActualizar);

      setAlertas(pedidosConAlerta);
    };

    calcularAlertas();
  }, [pedidos]);

  const choferActual = choferes.find(c => c.nombre === choferSeleccionado);
  
  // Filtrar todos los pedidos (ordenados por fecha más reciente primero)
  const todosLosPedidos = (pedidos && Array.isArray(pedidos)) 
    ? [...pedidos].sort((a, b) => {
        const fechaA = a.fechaCompra ? new Date(a.fechaCompra).getTime() : 0;
        const fechaB = b.fechaCompra ? new Date(b.fechaCompra).getTime() : 0;
        return fechaB - fechaA; // Más recientes primero
      })
    : [];
  
  // Filtrar pedidos por búsqueda de folio
  const pedidosFiltrados = busquedaFolio
    ? todosLosPedidos.filter(p => {
        const folio = (p.numeroDocumento || p.id || '').toString().toLowerCase();
        return folio.includes(busquedaFolio.toLowerCase());
      })
    : todosLosPedidos;
  
  // Calcular paginación
  const totalPaginas = Math.ceil(pedidosFiltrados.length / pedidosPorPagina);
  const indiceInicio = (paginaActual - 1) * pedidosPorPagina;
  const indiceFin = indiceInicio + pedidosPorPagina;
  const pedidosRecientes = pedidosFiltrados.slice(indiceInicio, indiceFin);
  
  // Funciones de navegación de paginación
  const irAPaginaAnterior = () => {
    if (paginaActual > 1) {
      setPaginaActual(paginaActual - 1);
    }
  };
  
  const irAPaginaSiguiente = () => {
    if (paginaActual < totalPaginas) {
      setPaginaActual(paginaActual + 1);
    }
  };
  
  // Resetear a página 1 cuando cambia la búsqueda
  useEffect(() => {
    setPaginaActual(1);
  }, [busquedaFolio]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES');
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const formatDateFlete = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES');
  };

  const getStatusPill = (estatus) => {
    const statusMap = {
      'entregado': { class: 'status-entregado', text: 'Entregado' },
      'en_preparacion': { class: 'status-pendiente', text: 'Pendiente' },
      'ruta': { class: 'status-pendiente', text: 'Pendiente' },
      'pendiente': { class: 'status-pendiente', text: 'Pendiente' },
      'cancelado': { class: 'status-cancelado', text: 'Cancelado' }
    };
    const status = statusMap[estatus] || { class: 'status-default', text: estatus };
    return <span className={`status-pill ${status.class}`}>{status.text}</span>;
  };

  const eliminarPedido = async (pedidoId) => {
    if (!window.confirm('¿Deseas eliminar esta orden?')) return;
    try {
      await PedidosService.eliminarPedido(pedidoId);
      setPedidos((prev) => prev.filter((p) => p.id !== pedidoId));
    } catch (error) {
      console.error('Error eliminando pedido:', error);
      alert('No se pudo eliminar la orden');
    }
  };

  // Calcular porcentajes para las cards
  const porcentajeEntregados = estadisticas.total > 0 ? Math.round((estadisticas.entregados / estadisticas.total) * 100) : 0;
  const porcentajePendientes = estadisticas.total > 0 ? Math.round((estadisticas.pendientes / estadisticas.total) * 100) : 0;
  const porcentajeCancelados = estadisticas.total > 0 ? Math.round((estadisticas.cancelados / estadisticas.total) * 100) : 0;

  // Datos para gráficos de distribución
  const datosDistribucion = [
    { label: 'Entregado', porcentaje: estadisticas.total > 0 ? (estadisticas.entregados / estadisticas.total * 100) : 0, color: '#28a745' },
    { label: 'Pendiente', porcentaje: estadisticas.total > 0 ? (estadisticas.pendientes / estadisticas.total * 100) : 0, color: '#ffc107' },
    { label: 'Cancelado', porcentaje: estadisticas.total > 0 ? (estadisticas.cancelados / estadisticas.total * 100) : 0, color: '#dc3545' }
  ];


  // Se eliminó la sección de ubicación en tiempo real

  // Métricas por repartidor seleccionado para gráfico de barras
  const pedidosChofer = pedidos.filter(p => choferSeleccionado && p.chofer === choferSeleccionado);
  const metricasChofer = {
    entregados: pedidosChofer.filter(p => p.estatus === 'entregado').length,
    pendientes: pedidosChofer.filter(p => p.estatus === 'pendiente' || p.estatus === 'en_preparacion' || p.estatus === 'ruta').length,
    cancelados: pedidosChofer.filter(p => p.estatus === 'cancelado').length
  };
  const maxMetricaChofer = Math.max(1, metricasChofer.entregados, metricasChofer.pendientes, metricasChofer.cancelados);

  // Métricas para todos los repartidores (barras apiladas)
  const statsPorChofer = choferes.map((c) => {
    const pedidosDeC = pedidos.filter(p => p.chofer === c.nombre);
    const entregados = pedidosDeC.filter(p => p.estatus === 'entregado').length;
    const pendientes = pedidosDeC.filter(p => p.estatus === 'pendiente' || p.estatus === 'en_preparacion' || p.estatus === 'ruta').length;
    const cancelados = pedidosDeC.filter(p => p.estatus === 'cancelado').length;
    const total = Math.max(1, entregados + pendientes + cancelados);
    return {
      nombre: c.nombre,
      entregados,
      pendientes,
      cancelados,
      total
    };
  });

  // Datos semanales dinámicos a partir de pedidos (conteo por semana del mes)
  const ahora = new Date();
  const primerDiaMes = new Date(ahora.getFullYear(), ahora.getMonth(), 1);
  const ultimoDiaMes = new Date(ahora.getFullYear(), ahora.getMonth() + 1, 0);
  
  // Calcular semanas del mes
  const semanas = [
    { inicio: 1, fin: 7, label: 'Semana 1' },
    { inicio: 8, fin: 14, label: 'Semana 2' },
    { inicio: 15, fin: 21, label: 'Semana 3' },
    { inicio: 22, fin: ultimoDiaMes.getDate(), label: 'Semana 4' }
  ];
  
  const datosSemanal = semanas.map(semana => {
    const pedidosSemana = pedidos.filter(p => {
      if (!p.fechaCompra) return false;
      const fechaPedido = new Date(p.fechaCompra);
      if (fechaPedido.getMonth() !== ahora.getMonth() || fechaPedido.getFullYear() !== ahora.getFullYear()) return false;
      const dia = fechaPedido.getDate();
      return dia >= semana.inicio && dia <= semana.fin;
    });
    
    // Calcular productividad basada en pedidos entregados
    const entregados = pedidosSemana.filter(p => p.estatus === 'entregado').length;
    const total = pedidosSemana.length;
    const productividad = total > 0 ? Math.round((entregados / total) * 100) : 0;
    
    return {
      semana: semana.label,
      productividad: productividad || 85 // Valor por defecto si no hay datos
    };
  });

  const maxPedidos = Math.max(...datosSemanal.map(d => d.productividad), 1);

  // Función para filtrar pedidos según el filtro de tiempo
  const filtrarPedidosPorFecha = () => {
    // Validar que pedidos existe y es un array
    if (!pedidos || !Array.isArray(pedidos)) {
      return [];
    }
    
    return pedidos.filter(pedido => {
      if (!pedido || !pedido.fechaCompra || pedido.estatus !== 'entregado') return false;
      
      const fechaPedido = new Date(pedido.fechaCompra);
      
      if (filtroTiempo === 'dia') {
        if (!fechaInicio || !fechaFin) return true;
        const inicio = new Date(fechaInicio);
        const fin = new Date(fechaFin);
        return fechaPedido >= inicio && fechaPedido <= fin;
      } else if (filtroTiempo === 'mes') {
        return fechaPedido.getMonth() + 1 === mesSeleccionado && 
               fechaPedido.getFullYear() === añoSeleccionado;
      } else if (filtroTiempo === 'año') {
        return fechaPedido.getFullYear() === añoSeleccionado;
      }
      return true;
    });
  };

  // Función para exportar productividad a Excel con formato empresarial y colores
  const exportarProductividadExcel = () => {
    const pedidosFiltrados = filtrarPedidosPorFecha();
    
    // Agrupar entregas por repartidor (cada documento único = 1 entrega)
    const entregasPorRepartidor = {};
    
    pedidosFiltrados.forEach(pedido => {
      const repartidor = pedido.chofer || 'Sin asignar';
      const numeroDocumento = pedido.numeroDocumento || (pedido.id ? `ORD-${pedido.id.slice(-3)}` : 'ORD-000');
      
      if (!entregasPorRepartidor[repartidor]) {
        entregasPorRepartidor[repartidor] = {
          nombre: repartidor,
          documentos: new Set(),
          detalles: []
        };
      }
      
      // Agregar documento único
      entregasPorRepartidor[repartidor].documentos.add(numeroDocumento);
      entregasPorRepartidor[repartidor].detalles.push({
        numeroDocumento,
        fecha: formatDate(pedido.fechaCompra)
      });
    });

    // Calcular total de entregas
    const totalEntregas = Object.values(entregasPorRepartidor).reduce(
      (sum, r) => sum + r.documentos.size, 0
    );

    // Determinar el título según el filtro
    let periodoTexto = '';
    if (filtroTiempo === 'dia') {
      periodoTexto = `${fechaInicio} al ${fechaFin}`;
    } else if (filtroTiempo === 'mes') {
      const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                     'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
      periodoTexto = `${meses[mesSeleccionado - 1]} ${añoSeleccionado}`;
    } else {
      periodoTexto = `Año ${añoSeleccionado}`;
    }

    // Generar HTML con estilos para Excel
    let htmlContent = `
      <html xmlns:x="urn:schemas-microsoft-com:office:excel">
      <head>
        <meta charset="UTF-8">
        <style>
          table { border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; }
          td, th { border: 1px solid #000; padding: 8px; text-align: center; }
          .titulo { background-color: #4472C4; color: white; font-size: 16px; font-weight: bold; }
          .fecha { font-style: italic; font-size: 11px; text-align: left; }
          .header-morado { background-color: #7030A0; color: white; font-weight: bold; font-size: 13px; }
          .header-azul { background-color: #00B0F0; color: white; font-weight: bold; font-size: 12px; }
          .total-azul { background-color: #00B0F0; color: white; font-weight: bold; border-top: 2px solid #000; border-bottom: 2px solid #000; }
          .dato { background-color: #FFFFFF; }
          .dato-alternado { background-color: #F2F2F2; }
        </style>
      </head>
      <body>
        <table>
          <!-- Título Principal -->
          <tr>
            <td colspan="3" class="titulo">Productividad del Área de Entregas - ${periodoTexto}</td>
          </tr>
          <!-- Fecha de Generación -->
          <tr>
            <td colspan="3" class="fecha">Fecha de generación: ${new Date().toLocaleDateString('es-MX', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</td>
          </tr>
          <!-- Espacio -->
          <tr><td colspan="3">&nbsp;</td></tr>
          
          <!-- RESUMEN GENERAL -->
          <tr>
            <td colspan="3" class="header-morado">RESUMEN GENERAL DEL ÁREA</td>
          </tr>
          <tr>
            <th class="header-morado">Empleado</th>
            <th class="header-morado">Total Entregas</th>
            <th class="header-morado">% del Total</th>
          </tr>`;
    
    // Agregar datos de repartidores
    Object.values(entregasPorRepartidor).forEach((repartidor, index) => {
      const totalEntregasRep = repartidor.documentos.size;
      const porcentaje = totalEntregas > 0 
        ? ((totalEntregasRep / totalEntregas) * 100).toFixed(2) 
        : 0;
      const rowClass = index % 2 === 0 ? 'dato' : 'dato-alternado';
      htmlContent += `
          <tr>
            <td class="${rowClass}">${repartidor.nombre}</td>
            <td class="${rowClass}">${totalEntregasRep}</td>
            <td class="${rowClass}">${porcentaje}%</td>
          </tr>`;
    });
    
    // Fila de total
    htmlContent += `
          <tr>
            <td class="total-azul">TOTAL ÁREA:</td>
            <td class="total-azul">${totalEntregas}</td>
            <td class="total-azul">100%</td>
          </tr>
          <tr><td colspan="3">&nbsp;</td></tr>`;
    
    // Detalle por empleado
    Object.values(entregasPorRepartidor).forEach(repartidor => {
      htmlContent += `
          <!-- Detalle de ${repartidor.nombre} -->
          <tr>
            <td colspan="3" class="header-azul">Detalle de ${repartidor.nombre.toUpperCase()}</td>
          </tr>
          <tr>
            <th class="header-morado">Número de Documento</th>
            <th class="header-morado" colspan="2">Entregas</th>
          </tr>`;
      
      // Documentos
      const documentosUnicos = Array.from(repartidor.documentos);
      documentosUnicos.forEach((doc, index) => {
        const rowClass = index % 2 === 0 ? 'dato' : 'dato-alternado';
        htmlContent += `
          <tr>
            <td class="${rowClass}">${doc}</td>
            <td class="${rowClass}" colspan="2">1</td>
          </tr>`;
      });
      
      // Total del repartidor
      htmlContent += `
          <tr>
            <td class="total-azul">TOTAL ${repartidor.nombre.toUpperCase()}:</td>
            <td class="total-azul" colspan="2">${repartidor.documentos.size}</td>
          </tr>
          <tr><td colspan="3">&nbsp;</td></tr>`;
    });
    
    htmlContent += `
        </table>
      </body>
      </html>`;
    
    // Crear blob y descargar
    const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `productividad_reparto_${periodoTexto.replace(/\s+/g, '_')}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="dashboard-gerente">
      {/* Header */}
      <header className="header">
        <div className="logo-section">
          <FiUser className="header-icon" />
          <span className="logo-text">Supervisor</span>
        </div>
        <div className="user-section">
          <FiMoon className="icon" />
          <button 
            className="logout-btn-header"
            onClick={() => {
              localStorage.removeItem('usuario');
              localStorage.removeItem('tipoUsuario');
              window.location.href = '/';
            }}
          >
            Salir
          </button>
          <div className="user-profile">
            <div className="avatar"><FiUser /></div>
            <span className="user-name">PATY</span>
          </div>
        </div>
      </header>

      <div className="main-container">
        {/* Sidebar */}
        <aside className="sidebar">
          <div className="logo-container">
            <div className="logo-icon"><FiHome /></div>
            <div className="logo-text-container">
              <div className="logo-main">MADERERIA LA HUERTA</div>
            </div>
          </div>
          <nav className="nav-menu">
            <div 
              className={`nav-item ${vistaActual === 'dashboard' ? 'active' : ''}`}
              onClick={() => setVistaActual('dashboard')}
            >
              <span className="nav-icon"><FiBarChart2 /></span>
              <span>Dashboard</span>
            </div>
            <div 
              className={`nav-item ${vistaActual === 'choferes' ? 'active' : ''}`}
              onClick={() => setVistaActual('choferes')}
            >
              <span className="nav-icon"><FiTruck /></span>
              <span>Gestionar Choferes</span>
            </div>
            <div 
              className={`nav-item ${vistaActual === 'combustible' ? 'active' : ''}`}
              onClick={() => setVistaActual('combustible')}
            >
              <span className="nav-icon"><FiClock /></span>
              <span>Combustible</span>
            </div>
            <div 
              className={`nav-item ${vistaActual === 'autos' ? 'active' : ''}`}
              onClick={() => setVistaActual('autos')}
            >
              <span className="nav-icon"><FiTruck /></span>
              <span>Autos</span>
            </div>
            <div 
              className={`nav-item ${vistaActual === 'mantenimiento' ? 'active' : ''}`}
              onClick={() => setVistaActual('mantenimiento')}
            >
              <span className="nav-icon"><FiTool /></span>
              <span>Mantenimientos</span>
            </div>
            <div 
              className={`nav-item ${vistaActual === 'alertas' ? 'active' : ''}`}
              onClick={() => setVistaActual('alertas')}
            >
              <span className="nav-icon"><FiAlertTriangle /></span>
              <span>Alertas</span>
              {alertas.length > 0 && (
                <span style={{
                  marginLeft: 'auto',
                  background: '#ef4444',
                  color: 'white',
                  borderRadius: '12px',
                  padding: '2px 8px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  {alertas.length}
                </span>
              )}
            </div>
            <div 
              className="nav-item logout-item"
              onClick={() => {
                localStorage.removeItem('usuario');
                localStorage.removeItem('tipoUsuario');
                window.location.href = '/';
              }}
              style={{ marginTop: 'auto', borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}
            >
              <span className="nav-icon"><FiLogOut /></span>
              <span>Cerrar Sesión</span>
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="main-content">
          {vistaActual === 'choferes' ? (
            <GestionChoferesGerente />
          ) : vistaActual === 'combustible' ? (
            <ReporteCombustible />
          ) : vistaActual === 'autos' ? (
            <FormularioAutos />
          ) : vistaActual === 'mantenimiento' ? (
            <ReporteMantenimiento />
          ) : vistaActual === 'alertas' ? (
            <GestionAlertas />
          ) : (
            <>
              {/* Estadísticas del Mes */}
              <section className="estadisticas-mes">
                <h2>Estadísticas del Mes</h2>
                <div className="charts-grid">
                  {/* Productividad Semanal */}
                  <div className="chart-wrapper">
                    <h3>Productividad Semanal</h3>
                    <div className="chart-card-line">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={datosSemanal}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="semana" />
                          <YAxis domain={[80, 100]} />
                          <RTooltip />
                          <Line type="monotone" dataKey="productividad" stroke="#2563eb" strokeWidth={2} name="Productividad" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Distribución por Departamento */}
                  <div className="chart-wrapper">
                    <h3>Distribución por Departamento</h3>
                    <div className="chart-card-donut">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie 
                            data={datosDistribucion} 
                            dataKey="porcentaje" 
                            nameKey="label" 
                            innerRadius={60} 
                            outerRadius={100} 
                            paddingAngle={2}
                          >
                            {datosDistribucion.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <RTooltip formatter={(value, name) => [`${value.toFixed(1)}%`, name]} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </section>

              {/* Estado de Producción y Alertas */}
              <section className="estado-produccion">
                <h2>Estado de Producción y Alertas</h2>
                <div className="alertas-cards">
                  <div className="alerta-card productividad">
                    <div className="alerta-icon"><FiTrendingUp /></div>
                    <div className="alerta-label">Productividad General</div>
                    <div className="alerta-value">{porcentajeEntregados}%</div>
                  </div>
                  <div className="alerta-card completados">
                    <div className="alerta-icon"><FiCheckCircle /></div>
                    <div className="alerta-label">Pedidos Completados</div>
                    <div className="alerta-value">{estadisticas.entregados}</div>
                  </div>
                  <div className="alerta-card pendientes-criticos">
                    <div className="alerta-icon"><FiAlertTriangle /></div>
                    <div className="alerta-label">Pendientes Críticos</div>
                    <div className="alerta-value">{estadisticas.pendientes}</div>
                  </div>
                </div>
              </section>


              {/* Sección de Pedidos Pendientes */}
              {mostrarPedidosPendientes && (
                <section className="pedidos-pendientes-dashboard">
                  <h2>Pedidos Pendientes ({pedidos.filter(p => p.estatus === 'pendiente').length})</h2>
                  {pedidos.filter(p => p.estatus === 'pendiente').length === 0 ? (
                    <div className="no-data">
                      <p>No hay pedidos pendientes</p>
                    </div>
                  ) : (
                    <div className="table-container">
                  <table className="ordenes-table">
                        <thead>
                          <tr>
                            <th>N Documento</th>
                            <th>Cliente</th>
                            <th>Fecha</th>
                            <th>Total</th>
                            <th>Razón de Pendiente</th>
                            <th>Fecha Pendiente</th>
                        <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pedidos.filter(p => p.estatus === 'pendiente').map((pedido) => (
                            <tr key={pedido.id}>
                              <td>{pedido.numeroDocumento || (pedido.id ? `ORD-${pedido.id.slice(-3)}` : 'ORD-000')}</td>
                              <td>{pedido.cliente}</td>
                              <td>{formatDate(pedido.fechaCompra)}</td>
                              <td>{formatCurrency(pedido.importe)}</td>
                              <td className="comentario-pendiente">{pedido.comentarioPendiente || 'Sin comentario'}</td>
                              <td>{formatDate(pedido.fechaPendiente)}</td>
                          <td>
                            <button className="btn-filtrar" onClick={() => eliminarPedido(pedido.id)}>Eliminar</button>
                          </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              )}

              {/* Sección de combustible movida a su vista dedicada */}

              {/* Sección de Ubicación en Tiempo Real eliminada */}

              {/* Órdenes Recientes */}
              <section className="ordenes-recientes">
                <div className="section-header">
                  <h2>Órdenes Recientes</h2>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="Buscar por folio..."
                      value={busquedaFolio}
                      onChange={(e) => setBusquedaFolio(e.target.value)}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        minWidth: '200px'
                      }}
                    />
                  </div>
                </div>
                <div className="table-container">
                  <table className="ordenes-table">
                    <thead>
                      <tr>
                        <th>N Documento</th>
                        <th>Cliente</th>
                        <th>Repartidor</th>
                        <th>Estado</th>
                        <th>Fecha</th>
                        <th>Total</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pedidosRecientes.map((pedido) => (
                        <tr key={pedido.id}>
                          <td>{pedido.numeroDocumento || (pedido.id ? `ORD-${pedido.id.slice(-3)}` : 'ORD-000')}</td>
                          <td>{pedido.cliente}</td>
                          <td>{pedido.chofer}</td>
                          <td>{getStatusPill(pedido.estatus)}</td>
                          <td>{formatDate(pedido.fechaCompra)}</td>
                          <td>{formatCurrency(pedido.importe)}</td>
                          <td>
                            <button className="btn-filtrar" onClick={() => eliminarPedido(pedido.id)}>Eliminar</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="pagination">
                  <span 
                    className={`pagination-arrow ${paginaActual === 1 ? 'disabled' : ''}`}
                    onClick={irAPaginaAnterior}
                    style={{ 
                      cursor: paginaActual === 1 ? 'not-allowed' : 'pointer',
                      opacity: paginaActual === 1 ? 0.5 : 1
                    }}
                  >
                    <FiArrowLeft />
                  </span>
                  <span className="pagination-info">
                    Página {paginaActual} de {totalPaginas || 1} ({pedidosFiltrados.length} pedidos)
                  </span>
                  <span 
                    className={`pagination-arrow ${paginaActual >= totalPaginas ? 'disabled' : ''}`}
                    onClick={irAPaginaSiguiente}
                    style={{ 
                      cursor: paginaActual >= totalPaginas ? 'not-allowed' : 'pointer',
                      opacity: paginaActual >= totalPaginas ? 0.5 : 1
                    }}
                  >
                    <FiArrowRight />
                  </span>
                </div>
              </section>

              {/* Desempeño de Repartidores */}
              <section className="desempeno-repartidores">
                <div className="section-header">
                  <h2>Desempeño de Repartidores</h2>
                  <button 
                    className="btn-exportar-excel"
                    onClick={exportarProductividadExcel}
                    title="Exportar a Excel"
                  >
                    <FiDownload /> Exportar a Excel
                  </button>
                </div>

                {/* Filtros de exportación */}
                <div className="filtros-productividad">
                  <div className="filtro-grupo">
                    <label>Período:</label>
                    <select 
                      value={filtroTiempo} 
                      onChange={(e) => setFiltroTiempo(e.target.value)}
                      className="filtro-select"
                    >
                      <option value="dia">Por Días</option>
                      <option value="mes">Por Mes</option>
                      <option value="año">Por Año</option>
                    </select>
                  </div>

                  {filtroTiempo === 'dia' && (
                    <>
                      <div className="filtro-grupo">
                        <label>Fecha Inicio:</label>
                        <input 
                          type="date" 
                          value={fechaInicio}
                          onChange={(e) => setFechaInicio(e.target.value)}
                          className="filtro-input"
                        />
                      </div>
                      <div className="filtro-grupo">
                        <label>Fecha Fin:</label>
                        <input 
                          type="date" 
                          value={fechaFin}
                          onChange={(e) => setFechaFin(e.target.value)}
                          className="filtro-input"
                        />
                      </div>
                    </>
                  )}

                  {filtroTiempo === 'mes' && (
                    <>
                      <div className="filtro-grupo">
                        <label>Mes:</label>
                        <select 
                          value={mesSeleccionado}
                          onChange={(e) => setMesSeleccionado(parseInt(e.target.value))}
                          className="filtro-select"
                        >
                          <option value="1">Enero</option>
                          <option value="2">Febrero</option>
                          <option value="3">Marzo</option>
                          <option value="4">Abril</option>
                          <option value="5">Mayo</option>
                          <option value="6">Junio</option>
                          <option value="7">Julio</option>
                          <option value="8">Agosto</option>
                          <option value="9">Septiembre</option>
                          <option value="10">Octubre</option>
                          <option value="11">Noviembre</option>
                          <option value="12">Diciembre</option>
                        </select>
                      </div>
                      <div className="filtro-grupo">
                        <label>Año:</label>
                        <input 
                          type="number" 
                          value={añoSeleccionado}
                          onChange={(e) => setAñoSeleccionado(parseInt(e.target.value))}
                          className="filtro-input"
                          min="2020"
                          max="2030"
                        />
                      </div>
                    </>
                  )}

                  {filtroTiempo === 'año' && (
                    <div className="filtro-grupo">
                      <label>Año:</label>
                      <input 
                        type="number" 
                        value={añoSeleccionado}
                        onChange={(e) => setAñoSeleccionado(parseInt(e.target.value))}
                        className="filtro-input"
                        min="2020"
                        max="2030"
                      />
                    </div>
                  )}
                </div>

                <div className="repartidor-content">
                  <div className="repartidor-selector">
                    <label>Seleccionar Repartidor</label>
                    <select 
                      value={choferSeleccionado}
                      onChange={(e) => setChoferSeleccionado(e.target.value)}
                    >
                      <option value="">Seleccionar...</option>
                      {choferes.map(chofer => (
                        <option key={chofer.id} value={chofer.nombre}>
                          {chofer.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Barras por cada repartidor */}
                  <div className="repartidores-bars">
                    {statsPorChofer.length === 0 && (
                      <div style={{ color: '#6b7280', fontSize: '0.95rem' }}>No hay datos de repartidores.</div>
                    )}
                    {statsPorChofer.map((st) => (
                      <div key={st.nombre} className="rep-row">
                        <div className="rep-name">{st.nombre}</div>
                        <div className="bar-track stacked">
                          <div className="bar-fill entregados" style={{ width: `${(st.entregados / st.total) * 100}%` }}>
                            <span className="bar-fill-label">{st.entregados}</span>
                          </div>
                          <div className="bar-fill pendientes" style={{ width: `${(st.pendientes / st.total) * 100}%` }}>
                            <span className="bar-fill-label">{st.pendientes}</span>
                          </div>
                          <div className="bar-fill cancelados" style={{ width: `${(st.cancelados / st.total) * 100}%` }}>
                            <span className="bar-fill-label">{st.cancelados}</span>
                          </div>
                        </div>
                        <div className="rep-values"></div>
                      </div>
                    ))}
                    {statsPorChofer.length > 0 && (
                      <div className="bar-legend">
                        <span><i className="legend-color entregados" /> Entregados</span>
                        <span><i className="legend-color pendientes" /> Pendientes</span>
                        <span><i className="legend-color cancelados" /> Cancelados</span>
                      </div>
                    )}
                  </div>

                  {choferActual && (
                    <div className="repartidor-card">
                      <div className="repartidor-avatar"><FiUser /></div>
                      <div className="repartidor-info">
                        <h3>{choferActual.nombre}</h3>
                        <div className="repartidor-stats">
                          <div className="stat-item">
                            <span className="stat-label">Pedidos Entregados:</span>
                            <span className="stat-value">{choferActual.pedidosEntregados || 0}</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Tiempo Prom. Entrega:</span>
                            <span className="stat-value">{choferActual.tiempoPromedioEntrega || 0} min</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Tasa de Éxito:</span>
                            <span className="stat-value">{Math.round(choferActual.tasaExito || 0)}%</span>
                          </div>
                        </div>

                        {/* Gráfico de barras por estatus del repartidor */}
                        <div className="bar-metrics">
                          <div className="bar-row">
                            <span className="bar-row-label">Entregados</span>
                            <div className="bar-track">
                              <div
                                className="bar-fill entregados"
                                style={{ width: `${(metricasChofer.entregados / maxMetricaChofer) * 100}%` }}
                              />
                            </div>
                            <span className="bar-row-value">{metricasChofer.entregados}</span>
                          </div>
                          <div className="bar-row">
                            <span className="bar-row-label">Pendientes</span>
                            <div className="bar-track">
                              <div
                                className="bar-fill pendientes"
                                style={{ width: `${(metricasChofer.pendientes / maxMetricaChofer) * 100}%` }}
                              />
                            </div>
                            <span className="bar-row-value">{metricasChofer.pendientes}</span>
                          </div>
                          <div className="bar-row">
                            <span className="bar-row-label">Cancelados</span>
                            <div className="bar-track">
                              <div
                                className="bar-fill cancelados"
                                style={{ width: `${(metricasChofer.cancelados / maxMetricaChofer) * 100}%` }}
                              />
                            </div>
                            <span className="bar-row-value">{metricasChofer.cancelados}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </section>

              {/* Distribución de Pedidos */}
              <section className="distribucion-pedidos">
                <h2>Distribución de Pedidos</h2>
                <div className="chart-container">
                  <div className="chart-card">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={datosDistribucion} dataKey="porcentaje" nameKey="label" innerRadius={50} outerRadius={80} paddingAngle={2}>
                          {datosDistribucion.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RTooltip formatter={(value, name) => [`${value.toFixed(1)}%`, name]} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="chart-total">
                    <div className="total-number">{estadisticas.total}</div>
                    <div className="total-label">Total</div>
                  </div>
                </div>
              </section>

              {/* Registro de Fletes */}
              <section className="fletes-section">
                <h2>Registro de Fletes ({fletes.length})</h2>
                {fletes.length === 0 ? (
                  <div className="no-data">
                    <p>No hay fletes registrados</p>
                  </div>
                ) : (
                  <div className="table-container">
                    <table className="ordenes-table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Destino Inicial</th>
                          <th>Destino Final</th>
                          <th>Chofer</th>
                          <th>Estatus</th>
                          <th>Monto</th>
                          <th>Flete Pagado</th>
                          <th>Tipo Unidad</th>
                          <th>Confirmado Por</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fletes.sort((a, b) => {
                          const fechaA = new Date(a.fecha || 0).getTime();
                          const fechaB = new Date(b.fecha || 0).getTime();
                          return fechaB - fechaA;
                        }).map((flete) => (
                          <tr key={flete.id}>
                            <td>{formatDateFlete(flete.fecha)}</td>
                            <td>{flete.destinoInicial || '-'}</td>
                            <td>{flete.destinoFinal || '-'}</td>
                            <td>{flete.chofer || '-'}</td>
                            <td>
                              <span className={`status-pill status-${flete.estatus?.toLowerCase().replace(' ', '-') || 'default'}`}>
                                {flete.estatus || '-'}
                              </span>
                            </td>
                            <td>{formatCurrency(flete.monto)}</td>
                            <td>{flete.fletePagado || 'No'}</td>
                            <td>{flete.tipoUnidad || '-'}</td>
                            <td>{flete.confirmadoPor || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* Rendimiento Semanal - Eliminado, ya está en Estadísticas del Mes */}
            </>
          )}
        </main>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-left">
          <a href="#ayuda">Ayuda</a>
          <a href="#legal">Legal</a>
        </div>
        <div className="footer-right">
          <span className="social-icon"><FaFacebookF /></span>
          <span className="social-icon"><FaTwitter /></span>
          <span className="social-icon"><FaInstagram /></span>
          <span className="social-icon"><FaLinkedinIn /></span>
        </div>
      </footer>
      {/* Modal de mapa eliminado */}
    </div>
  );
};

export default DashboardGerente;